import { useCallback, useEffect, useRef } from "react";
import numeral from "numeral";

interface UseNumeralInputOptions {
  suffix?: string;
  format?: string;
  allowDecimals?: boolean;
  selectAllOnFocus?: boolean;
  maxDigits?: number;
  onChange?: (rawValue: string, numericValue: number | null) => void;
  value?: string | number;
}

export const useNumeralInput = (options: UseNumeralInputOptions = {}) => {
  const {
    suffix = "kr",
    format = "0,0",
    allowDecimals = false,
    selectAllOnFocus = true,
    maxDigits = 15, // Default limit to prevent MAX_SAFE_INTEGER overflow
    onChange,
    value = ""
  } = options;

  const inputRef = useRef<HTMLInputElement>(null);
  const rawValueRef = useRef<string>(String(value));

  // Helper to count digits in a value
  const getDigitCount = useCallback((value: string) => {
    return value.replace(/[^0-9]/g, "").length;
  }, []);

  // Helper to check if value exceeds digit limit
  const exceedsDigitLimit = useCallback(
    (value: string) => {
      return getDigitCount(value) > maxDigits;
    },
    [getDigitCount, maxDigits]
  );

  const parseInput = useCallback(
    (input: string) => {
      if (!input) return "";

      // Remove suffix
      let cleaned = input.toString().replace(` ${suffix}`, "").replace(suffix, "");

      // Keep only allowed characters
      const allowedChars = allowDecimals ? /[^\d.,-]/g : /[^\d-]/g;
      cleaned = cleaned.replace(allowedChars, "");

      // Handle decimal comma -> decimal point
      if (allowDecimals) {
        cleaned = cleaned.replace(",", ".");
        // Fix multiple decimal points
        const parts = cleaned.split(".");
        if (parts.length > 2) {
          cleaned = parts[0] + "." + parts.slice(1).join("");
        }
      }

      return cleaned;
    },
    [suffix, allowDecimals]
  );

  const formatValue = useCallback(
    (value: string) => {
      if (!value || value === "" || value === "-") return value;

      try {
        const formatted = numeral(value).format(format);
        return formatted;
      } catch (e) {
        return value;
      }
    },
    [format]
  );

  // Helper function to map cursor position from raw value to formatted value
  const mapCursorPosition = useCallback(
    (rawValue: string, cursorPosition: number, formattedValue: string) => {
      if (!formattedValue || cursorPosition === 0) return cursorPosition;

      // Count spaces before the cursor position in the formatted value
      let spaceCount = 0;
      let digitCount = 0;

      for (let i = 0; i < formattedValue.length && digitCount < cursorPosition; i++) {
        if (formattedValue[i] === " ") {
          spaceCount++;
        } else {
          digitCount++;
        }
      }

      return cursorPosition + spaceCount;
    },
    []
  );

  const updateDisplay = useCallback(
    (rawValue: string, cursorPosition: number | null = null) => {
      const input = inputRef.current;
      if (!input) return;

      if (rawValue === "" || rawValue === "-") {
        input.value = rawValue;
      } else {
        const formattedValue = formatValue(rawValue);
        input.value = formattedValue ? `${formattedValue} ${suffix}` : rawValue;
      }

      // Restore cursor position
      if (cursorPosition !== null && input === document.activeElement) {
        setTimeout(() => {
          const displayValue = input.value.replace(` ${suffix}`, "");
          const maxPosition = displayValue.length;
          const newPosition = Math.min(cursorPosition, maxPosition);

          // Map cursor position to account for thousand separators
          const mappedPosition = mapCursorPosition(rawValue, newPosition, displayValue);
          input.setSelectionRange(mappedPosition, mappedPosition);
        }, 0);
      }

      // Call onChange if value changed
      if (rawValueRef.current !== rawValue) {
        rawValueRef.current = rawValue;
        const numericValue = numeral(rawValue).value();
        onChange?.(rawValue, numericValue);
      }
    },
    [formatValue, suffix, onChange, mapCursorPosition]
  );

  // Helper function to map cursor position from formatted value to raw value
  const mapCursorPositionFromFormatted = useCallback(
    (formattedValue: string, cursorPosition: number) => {
      if (!formattedValue || cursorPosition === 0) return cursorPosition;

      // Count digits up to the cursor position, ignoring spaces
      let digitCount = 0;
      for (let i = 0; i < Math.min(cursorPosition, formattedValue.length); i++) {
        if (formattedValue[i] !== " ") {
          digitCount++;
        }
      }

      return digitCount;
    },
    []
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.currentTarget.value;
      const cursorPosition = e.currentTarget.selectionStart;
      const displayValue = input.replace(` ${suffix}`, "");

      const cleanedValue = parseInput(input);

      // Don't update if too many digits
      if (exceedsDigitLimit(cleanedValue)) {
        return;
      }

      const mappedCursorPosition =
        cursorPosition !== null
          ? mapCursorPositionFromFormatted(displayValue, cursorPosition)
          : null;
      updateDisplay(cleanedValue, mappedCursorPosition);
    },
    [parseInput, updateDisplay, suffix, mapCursorPositionFromFormatted, exceedsDigitLimit]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const { key, currentTarget, ctrlKey, metaKey } = e;
      const cursorPosition = currentTarget.selectionStart;
      const displayValue = inputRef.current?.value.replace(` ${suffix}`, "") || "";

      const controlKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "Tab",
        "Home",
        "End",
        "Escape",
        "Enter"
      ];

      // Allow control keys and shortcuts
      if (controlKeys.includes(key) || ctrlKey || metaKey) {
        // Prevent arrow right from going into suffix
        if (
          key === "ArrowRight" &&
          cursorPosition !== null &&
          cursorPosition >= displayValue.length
        ) {
          e.preventDefault();
        }
        return;
      }

      // Allow digits (with limit check)
      if (key >= "0" && key <= "9") {
        if (getDigitCount(rawValueRef.current || "") >= maxDigits) {
          e.preventDefault();
        }
        return;
      }

      // Allow minus at start and decimal if enabled
      if (
        (key === "-" && cursorPosition === 0) ||
        (allowDecimals && (key === "." || key === ","))
      ) {
        return;
      }

      // Block everything else
      e.preventDefault();
    },
    [suffix, allowDecimals, getDigitCount, maxDigits]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData("text");
      const cleanedText = parseInput(pastedText);

      if (cleanedText && !exceedsDigitLimit(cleanedText)) {
        updateDisplay(cleanedText);
      }
    },
    [parseInput, updateDisplay, exceedsDigitLimit]
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (selectAllOnFocus && rawValueRef.current) {
        setTimeout(() => {
          const displayValue = e.target.value.replace(` ${suffix}`, "");
          const mappedEndPosition = mapCursorPosition(
            rawValueRef.current,
            displayValue.length,
            displayValue
          );
          e.target.setSelectionRange(0, mappedEndPosition);
        }, 0);
      }
    },
    [selectAllOnFocus, suffix, mapCursorPosition]
  );

  // Initialize with default value
  useEffect(() => {
    if (value && inputRef.current) {
      updateDisplay(String(value));
    }
  }, [value, updateDisplay]);

  const setValue = useCallback(
    (value: string | number) => {
      const stringValue = String(value);
      if (!exceedsDigitLimit(stringValue)) {
        updateDisplay(stringValue);
      }
    },
    [updateDisplay, exceedsDigitLimit]
  );

  const getValue = useCallback(() => {
    return rawValueRef.current;
  }, []);

  const getNumericValue = useCallback(() => {
    return numeral(rawValueRef.current).value();
  }, []);

  const clear = useCallback(() => {
    updateDisplay("");
  }, [updateDisplay]);

  const inputProps = {
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onPaste: handlePaste,
    onFocus: handleFocus
  };

  return {
    inputProps,
    inputRef,
    getValue,
    getNumericValue,
    setValue,
    clear,
    isEmpty: () => rawValueRef.current === ""
  };
};
